import React, { forwardRef } from 'react'
import { makeStyles } from '@mui/styles'
import theme from '@/theme'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import lang from '@/lang'
import ToastFrame from './toast-frame'
import type { SubscribeToToastStack, ToastKey } from './use-toast-controller'

// icons for states
import SuccessIcon from '@assets/images/icons/check.svg'
import WarningIcon from '@assets/images/icons/exclamation_mark2.svg'
import ErrorIcon from '@assets/images/icons/cancel.svg'

// icons for info from the application
import Logo from '@assets/images/notifications/logo.svg'
import SentIcon from '@assets/images/notifications/sent.svg'
import PaidIcon from '@assets/images/notifications/paid.svg'
import HandshakeIcon from '@assets/images/notifications/handshake.svg'
import BankingIcon from '@assets/images/notifications/banking.svg'
import AlertIcon from '@assets/images/notifications/alert.svg'

const { messages: { common: { toastHeadings: headings } } } = lang

type ToastType = 'success' | 'warning' | 'error' | 'info'
type ToastInfoIcon = 'handshake' | 'at' | 'alert' | 'banking' | 'euro'
type SvgComponent = React.ComponentType<React.SVGProps<SVGSVGElement>>

interface InfoIcon {
  file: SvgComponent
  width?: string
  height?: string
}

interface ToastIconSetting {
  file?: SvgComponent
  width?: string
  height?: string
  fill?: string
}

interface ToastSetting {
  icon: ToastIconSetting
  heading: string
  accentColor: string
  iconBubbleBackground: string
}

type ToastSettings = Record<ToastType, ToastSetting>

interface ToastTemplateProps {
  snackBarKey: ToastKey
  message: string
  title?: string
  type: ToastType
  icon?: ToastInfoIcon
  autoHideDuration?: number | null
  subscribeToToastStack?: SubscribeToToastStack
  removeToast?: (key: ToastKey) => void
}

interface ToastTemplateStyleProps {
  toastSettings: ToastSettings
  type: ToastType
}

const useStyles = makeStyles(() => ({
  toast__container: {
    display: 'flex',
    height: '100%',
    alignItems: 'center',
    paddingLeft: 20,
    position: 'relative',
  },
  toast__content: {
    display: 'flex',
    flexDirection: 'column',
    margin: 'auto 0',
    paddingLeft: 20,
    paddingRight: 30,
    paddingTop: 10,
    paddingBottom: 10,
  },
  toast__heading: {
    fontSize: theme.fontSizes.medium,
    color: theme.textColor,
    marginBottom: 7,
  },
  toast__text: {
    fontSize: theme.fontSizes.default,
    color: theme.textMuted,
    '& h6': {
      fontSize: 16,
      fontWeight: 400,
      margin: '8px 0 4px 0',
      color: theme.textColor,
    },
  },
  toast__textWithoutHeading: {
    fontSize: theme.fontSizes.default,
  },
  toast__iconBubble: ({ toastSettings, type }: ToastTemplateStyleProps) => {
    return {
      width: 40,
      height: 40,
      flex: '0 0 40px',
      borderRadius: '50%',
      backgroundColor: toastSettings[type].iconBubbleBackground,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      margin: 0,
      '& > *': {
        margin: 'auto',
      },
    }
  },
}))

const ToastTemplate = forwardRef<HTMLElement, ToastTemplateProps>(({ snackBarKey, type, message, title, icon, autoHideDuration, subscribeToToastStack, removeToast }, ref) => {
  let infoIcon: InfoIcon | undefined
  if (type === 'info') {
    switch (icon) {
      case 'handshake':
        infoIcon = {
          file: HandshakeIcon,
        }
        break

      case 'at':
        infoIcon = {
          file: SentIcon,
        }
        break

      case 'alert':
        infoIcon = {
          file: AlertIcon,
        }
        break

      case 'banking':
        infoIcon = {
          file: BankingIcon,
        }
        break

      case 'euro':
        infoIcon = {
          file: PaidIcon,
        }
        break

      default: // -> logo
        infoIcon = {
          file: Logo,
          width: '22px',
          height: '22px',
        }
        break
    }
  }

  const toastSettings: ToastSettings = {
    success: {
      icon: {
        file: SuccessIcon,
        width: '22px',
        height: '17px',
        fill: theme.white,
      },
      heading: headings.success,
      accentColor: theme.green,
      iconBubbleBackground: theme.green,
    },
    warning: {
      icon: {
        file: WarningIcon,
        width: '17px',
        height: '17px',
        fill: theme.white,
      },
      heading: headings.warning,
      accentColor: theme.orange,
      iconBubbleBackground: theme.orange,
    },
    error: {
      icon: {
        file: ErrorIcon,
        width: '17px',
        height: '17px',
        fill: theme.white,
      },
      heading: headings.error,
      accentColor: theme.error,
      iconBubbleBackground: theme.error,
    },
    info: {
      icon: {
        file: infoIcon?.file,
        width: '22px',
        height: '22px',
      },
      heading: headings.info,
      accentColor: theme.lighterBlue,
      iconBubbleBackground: theme.white,
    },
  }

  const toastSetting = toastSettings[type]
  const classes = useStyles({ toastSettings, type })
  const selectedIcon = infoIcon?.file || toastSetting.icon.file

  const heading = title || (
    type !== 'success' && type !== 'error' && type!== 'warning'
      ? toastSetting.heading
      : null
  )

  return (
    <ToastFrame
      ref={ref}
      snackBarKey={snackBarKey}
      type={type}
      accentColor={toastSetting.accentColor}
      autoHideDuration={autoHideDuration}
      subscribeToToastStack={subscribeToToastStack}
      removeToast={removeToast}
    >
      <div className={classes.toast__container}>
        {selectedIcon
      && <span className={classes.toast__iconBubble}>
        <SVGIcon
          svg={selectedIcon}
          height={infoIcon?.height || toastSetting.icon.height}
          width={infoIcon?.width || toastSetting.icon.width}
          fill={toastSetting.icon.fill}
        />
      </span>
        }
        <div className={classes.toast__content}>
          {heading
            && <span className={classes.toast__heading}>{heading}</span>
          }
          <span
            dangerouslySetInnerHTML={{ __html: message }}
            className={`${classes.toast__text} ${!heading ? classes.toast__textWithoutHeading : ''}`}
          />
        </div>
      </div>
    </ToastFrame>
  )
})

export default ToastTemplate